import { QueryDocumentSnapshot } from 'firebase/firestore';
import { LostAndFoundItem, ItemStatus, ItemCategory, ItemCondition } from '@/types/community/lostAndFound';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  updateDoc, 
  deleteDoc, 
  query, 
  where, 

  setDoc
} from 'firebase/firestore';
import { addWorkspaceAndActivity, createActivityRecord, isDemoWorkspace } from '@/utils/apiHelpers';
import { User } from '../types/users';
import { formatFirestoreTimestamp } from '@/utils/date';
import { removeUndefined } from '@/utils/removeUndefined';
import { loadMockData } from '@/mock';

// Helper functions for mock data
function filterMockData<T extends { title?: string; description?: string; category?: string; status?: string; condition?: string; claimedBy?: { id?: string }; reportedBy?: { id?: string }; handedOverTo?: { id?: string }; block?: { id?: string }; apartmentUnit?: { id?: string } }>(
  data: T[],
  searchTerm?: string,
  category?: string,
  status?: string,
  condition?: string,
  claimedBy?: string,
  reportedBy?: string,
  handedOverTo?: string,
  blockId?: string,
  apartmentUnitId?: string
): T[] {
  return data.filter(item => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (item.title && item.title.toLowerCase().includes(searchLower)) ||
        (item.description && item.description.toLowerCase().includes(searchLower));
      if (!matchesSearch) return false;
    }
    
    if (category && item.category !== category) return false;
    if (status && item.status !== status) return false;
    if (condition && item.condition !== condition) return false;
    if (claimedBy && item.claimedBy?.id !== claimedBy) return false;
    if (reportedBy && item.reportedBy?.id !== reportedBy) return false;
    if (handedOverTo && item.handedOverTo?.id !== handedOverTo) return false;
    if (blockId && item.block?.id !== blockId) return false;
    if (apartmentUnitId && item.apartmentUnit?.id !== apartmentUnitId) return false;
    
    return true;
  });
}

function paginateMockData<T>(
  data: T[],
  pageSize?: number,
  currentPage: number = 1
): { data: T[]; total: number } {
  if (!pageSize) {
    return { data, total: data.length };
  }
  
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);
  
  return {
    data: paginatedData,
    total: data.length
  };
}

// Constants
const LOST_AND_FOUND_COLLECTION = 'lost_and_found';

// Types
export interface LostAndFoundQueryParams {
  searchTerm?: string;
  category?: ItemCategory;
  status?: ItemStatus;
  condition?: ItemCondition;
  claimedBy?: string;
  reportedBy?: string;
  handedOverTo?: string;
  blockId?: string;
  apartmentUnitId?: string;
  pageSize?: number;
  page?: number;
  lastDoc?: QueryDocumentSnapshot;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  firestoreUser: User;
}

type LostAndFoundItemWithDates = Partial<LostAndFoundItem>;

// Lost and Found API
export async function getLostAndFoundItems({
  searchTerm,
  category,
  status,
  condition,
  claimedBy,
  reportedBy,
  handedOverTo,
  blockId,
  apartmentUnitId,
  pageSize,
  page = 1,
  lastDoc,
  sortBy,
  sortOrder,
  firestoreUser
}: LostAndFoundQueryParams): Promise<{ items: LostAndFoundItem[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('DEMO MODE: Fetching mock lost and found items with filters', { searchTerm, category, status, page, pageSize });
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockItems = await loadMockData<LostAndFoundItem[]>('lostAndFoundItems', firestoreUser.language);
    const filteredData = filterMockData(mockItems, searchTerm, category, status, condition, claimedBy, reportedBy, handedOverTo, blockId, apartmentUnitId);
    const paginatedData = paginateMockData(filteredData, pageSize, page);
    
    return {
      items: paginatedData.data,
      lastDoc: undefined, // Mock data doesn't have Firestore documents
      total: paginatedData.total
    };
  }
  
  console.log('API Call: getLostAndFoundItems', { firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Simplified query - only filter by workspace
    const itemsRef = collection(db, LOST_AND_FOUND_COLLECTION);
    const q = query(
      itemsRef,
      where('workspace.id', '==', firestoreUser.workspace.id)
    );

    const snapshot = await getDocs(q);
    const items = snapshot.docs
      .map(doc => {
        const data = doc.data() as LostAndFoundItemWithDates;
        const mapped: LostAndFoundItemWithDates = {
          ...data,
          id: doc.id,
          foundAt: data.foundAt ? formatFirestoreTimestamp(data.foundAt) ?? undefined : undefined,
          returnedAt: data.returnedAt ? formatFirestoreTimestamp(data.returnedAt) ?? undefined : undefined
        };
        // Only return if all required fields are present
        if (
          typeof mapped.title === 'string' &&
          typeof mapped.description === 'string' &&
          typeof mapped.category === 'string' &&
          typeof mapped.status === 'string' &&
          typeof mapped.condition === 'string' &&
          mapped.foundAt instanceof Date &&
          mapped.workspace && typeof mapped.workspace.id === 'string'
        ) {
          return mapped as LostAndFoundItem;
        }
        return undefined;
      })
      .filter((i): i is LostAndFoundItem => !!i);

    // Apply all filters client-side
    let filteredItems = items;

    // Apply search filter
    if (searchTerm) {
      filteredItems = filteredItems.filter(item => 
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.claimedBy?.name && item.claimedBy.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.reportedBy?.name && item.reportedBy.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.handedOverTo?.name && item.handedOverTo.name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply category filter
    if (category) {
      filteredItems = filteredItems.filter(item => item.category === category);
    }

    // Apply status filter
    if (status) {
      filteredItems = filteredItems.filter(item => item.status === status);
    }

    // Apply condition filter
    if (condition) {
      filteredItems = filteredItems.filter(item => item.condition === condition);
    }

    // Apply claimedBy filter
    if (claimedBy) {
      filteredItems = filteredItems.filter(item => item.claimedBy?.id === claimedBy);
    }

    // Apply reportedBy filter
    if (reportedBy) {
      filteredItems = filteredItems.filter(item => item.reportedBy?.id === reportedBy);
    }

    // Apply handedOverTo filter
    if (handedOverTo) {
      filteredItems = filteredItems.filter(item => item.handedOverTo?.id === handedOverTo);
    }

    // Apply blockId filter
    if (blockId) {
      filteredItems = filteredItems.filter(item => item.block?.id === blockId);
    }

    // Apply apartmentUnitId filter
    if (apartmentUnitId) {
      filteredItems = filteredItems.filter(item => item.apartmentUnit?.id === apartmentUnitId);
    }

    // Apply sorting client-side
    if (sortBy) {
      filteredItems.sort((a, b) => {
        const aValue = (a as any)[sortBy];
        const bValue = (b as any)[sortBy];
        
        if (aValue instanceof Date && bValue instanceof Date) {
          return sortOrder === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }
        
        return 0;
      });
    } else {
      // Default sort by foundAt desc
      filteredItems.sort((a, b) => b.foundAt.getTime() - a.foundAt.getTime());
    }

    // Apply pagination client-side
    let paginatedItems = filteredItems;
    if (pageSize) {
      const startIndex = lastDoc ? 0 : 0; // For now, always start from beginning
      paginatedItems = filteredItems.slice(startIndex, startIndex + pageSize);
    }

    return {
      items: paginatedItems,
      lastDoc: undefined, // Not implementing pagination for now
      total: filteredItems.length
    };
  } catch (error) {
    console.error('Error fetching lost and found items:', error);
    throw new Error('Failed to fetch lost and found items');
  }
}

export async function getLostAndFoundItemById(id: string, firestoreUser: User): Promise<LostAndFoundItem | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log(`DEMO MODE: Fetching mock lost and found item with id: ${id}`);
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockItems = await loadMockData<LostAndFoundItem[]>('lostAndFoundItems', firestoreUser.language);
    const item = mockItems.find(i => i.id === id) || null;
    return item;
  }
  
  console.log('API Call: getLostAndFoundItemById', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the item document directly by ID
    const itemRef = doc(db, LOST_AND_FOUND_COLLECTION, id);
    const itemDoc = await getDoc(itemRef);
    
    if (!itemDoc.exists()) {
      return null;
    }

    const itemData = itemDoc.data() as LostAndFoundItemWithDates;
    
    // Check if the item belongs to the current workspace
    if (!itemData.workspace || itemData.workspace.id !== firestoreUser.workspace.id) {
      return null;
    }

    const mappedItem: LostAndFoundItemWithDates = {
      ...itemData,
      id: itemDoc.id,
      foundAt: itemData.foundAt ? formatFirestoreTimestamp(itemData.foundAt) ?? undefined : undefined,
      returnedAt: itemData.returnedAt ? formatFirestoreTimestamp(itemData.returnedAt) ?? undefined : undefined
    };
    if (
      typeof mappedItem.title === 'string' &&
      typeof mappedItem.description === 'string' &&
      typeof mappedItem.category === 'string' &&
      typeof mappedItem.status === 'string' &&
      typeof mappedItem.condition === 'string' &&
      mappedItem.foundAt instanceof Date &&
      mappedItem.workspace && typeof mappedItem.workspace.id === 'string'
    ) {
      return mappedItem as LostAndFoundItem;
    }
    return null;
  } catch (error) {
    console.error('Error fetching lost and found item:', error);
    throw new Error('Failed to fetch lost and found item');
  }
}

export async function addLostAndFoundItem(
  item: Omit<LostAndFoundItem, 'id'>,
  firestoreUser: User
): Promise<string> {
  console.log('API Call: addLostAndFoundItem', { item, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Validate required fields
    if (!item.title?.trim()) {
      throw new Error('Item title is required');
    }
    if (!item.description?.trim()) {
      throw new Error('Item description is required');
    }
    if (!item.category) {
      throw new Error('Item category is required');
    }
    if (!item.status) {
      throw new Error('Item status is required');
    }
    if (!item.condition) {
      throw new Error('Item condition is required');
    }
    if (!item.foundAt) {
      throw new Error('Item found date is required');
    }

    const itemData = addWorkspaceAndActivity(
      {
        ...item,
        foundAt: item.foundAt,
        returnedAt: item.returnedAt,
        block: item.block || undefined,
        apartmentUnit: item.apartmentUnit || undefined,
        photos: item.photos || undefined,
        value: item.value || undefined,
        reportedBy: item.reportedBy || undefined,
        claimedBy: item.claimedBy || undefined,
        handedOverTo: item.handedOverTo || undefined,
        activityRecords: item.activityRecords || undefined
      },
      firestoreUser,
      'created'
    );

    const itemsRef = collection(db, LOST_AND_FOUND_COLLECTION);
    const newItemRef = doc(itemsRef);
    
    const finalData = removeUndefined({
      ...itemData,
      id: newItemRef.id
    });

    console.log('Saving lost and found item data:', finalData);

    await setDoc(newItemRef, finalData);

    return newItemRef.id;
  } catch (error: any) {
    console.error('Error adding lost and found item:', error);
    
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: You do not have access to create lost and found items');
    } else if (error.code === 'unavailable') {
      throw new Error('Service unavailable: Please try again later');
    } else if (error.code === 'invalid-argument') {
      throw new Error('Invalid data provided: Please check all required fields');
    } else if (error.message) {
      throw new Error(`Failed to add lost and found item: ${error.message}`);
    } else {
      throw new Error('Failed to add lost and found item: Unknown error occurred');
    }
  }
}

export async function updateLostAndFoundItem(
  id: string,
  data: Partial<Omit<LostAndFoundItem, 'id'>>,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: updateLostAndFoundItem', { id, data, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    if (!id) {
      throw new Error('Item ID is required for update');
    }

    // Get existing item to create activity record
    const existingItem = await getLostAndFoundItemById(id, firestoreUser);
    if (!existingItem) {
      throw new Error('Lost and found item not found');
    }

    const activityRecord = createActivityRecord('updated', data, firestoreUser);

    const updateData = removeUndefined({
      ...data,
      foundAt: data.foundAt,
      returnedAt: data.returnedAt,
      block: data.block || undefined,
      apartmentUnit: data.apartmentUnit || undefined,
      photos: data.photos || undefined,
      value: data.value || undefined,
      reportedBy: data.reportedBy || undefined,
      claimedBy: data.claimedBy || undefined,
      handedOverTo: data.handedOverTo || undefined,
      comments: data.comments || existingItem.comments || undefined,
      activityRecords: [activityRecord]
    });

    console.log('Updating lost and found item data:', updateData);

    // Update the document directly by ID
    const itemRef = doc(db, LOST_AND_FOUND_COLLECTION, id);
    await updateDoc(itemRef, updateData);
  } catch (error: any) {
    console.error('Error updating lost and found item:', error);
    
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: You do not have access to update lost and found items');
    } else if (error.code === 'not-found') {
      throw new Error('Lost and found item not found');
    } else if (error.message) {
      throw new Error(`Failed to update lost and found item: ${error.message}`);
    } else {
      throw new Error('Failed to update lost and found item: Unknown error occurred');
    }
  }
}

export async function deleteLostAndFoundItem(
  id: string,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: deleteLostAndFoundItem', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the item to verify it exists and belongs to the workspace
    const existingItem = await getLostAndFoundItemById(id, firestoreUser);
    if (!existingItem) {
      throw new Error('Lost and found item not found');
    }

    // Delete the document directly by ID
    const itemRef = doc(db, LOST_AND_FOUND_COLLECTION, id);
    await deleteDoc(itemRef);
  } catch (error: any) {
    console.error('Error deleting lost and found item:', error);
    
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: You do not have access to delete lost and found items');
    } else if (error.code === 'not-found') {
      throw new Error('Lost and found item not found');
    } else if (error.message) {
      throw new Error(`Failed to delete lost and found item: ${error.message}`);
    } else {
      throw new Error('Failed to delete lost and found item: Unknown error occurred');
    }
  }
}

export async function getLostAndFoundItemsByStatus(status: ItemStatus, firestoreUser: User): Promise<LostAndFoundItem[]> {
  console.log('API Call: getLostAndFoundItemsByStatus', { status, firestoreUser });
  
  const { items } = await getLostAndFoundItems({ status, firestoreUser });
  return items;
}

export async function getLostAndFoundItemsByCategory(category: ItemCategory, firestoreUser: User): Promise<LostAndFoundItem[]> {
  console.log('API Call: getLostAndFoundItemsByCategory', { category, firestoreUser });
  
  const { items } = await getLostAndFoundItems({ category, firestoreUser });
  return items;
}

export async function getLostAndFoundItemsByCondition(condition: ItemCondition, firestoreUser: User): Promise<LostAndFoundItem[]> {
  console.log('API Call: getLostAndFoundItemsByCondition', { condition, firestoreUser });
  
  const { items } = await getLostAndFoundItems({ condition, firestoreUser });
  return items;
} 